import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { fetchAdminBlogBySlug, createAdminBlog, updateAdminBlog } from '../../../store/slices/blogAdminSlice';

// ✅ CKEditor 5 (same as CMS)
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

const BlogEditor = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const dispatch = useAppDispatch();

    const { currentBlog, loading } = useAppSelector((state) => state.blogAdmin);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [imageFile, setImageFile] = useState(null);

    const [formData, setFormData] = useState({
        title: '',
        author: '',
        keyword: '',
        content: '',
        status: 'Published',
        page_url: ''
    });

    const [errors, setErrors] = useState({});
    const [imagePreview, setImagePreview] = useState(null);

    // Fetch blog if editing
    useEffect(() => {
        if (id) {
            dispatch(fetchAdminBlogBySlug(id));
        }
    }, [id, dispatch]);

    // Populate form
    useEffect(() => {
        if (id && currentBlog) {
            setFormData({
                title: currentBlog.title || '',
                author: currentBlog.created_by || currentBlog.author || '',
                keyword: currentBlog.keyword || '',
                content: currentBlog.page_content || '',
                status: currentBlog.status || 'Published',
                page_url: currentBlog.page_url || ''
            });

            // Use presigned URL if available, otherwise fall back to image key
            setImagePreview(currentBlog.image_url || (currentBlog.image ? `/storage/blogs/${currentBlog.image}` : null));
        }
    }, [currentBlog, id]);

    const handleInputChange = (e) => {
        const { name, value, files } = e.target;

        if (name === 'image') {
            const file = files[0] || null;
            setImageFile(file);
            
            // Show preview
            if (file) {
                const reader = new FileReader();
                reader.onload = (event) => {
                    setImagePreview(event.target.result);
                };
                reader.readAsDataURL(file);
            }
        } else if (name === 'title') {
            // Auto-generate page_url from title
            const slug = value
                .toLowerCase()
                .trim()
                .replace(/[^\w\s-]/g, '')
                .replace(/\s+/g, '-')
                .replace(/-+/g, '-');
            setFormData(prev => ({ ...prev, [name]: value, page_url: slug }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    // Validation
    const validateForm = () => {
        const newErrors = {};

        if (!formData.title.trim()) newErrors.title = 'Blog Title is required';
        if (!formData.author.trim()) newErrors.author = 'Author is required';
        if (!formData.keyword.trim()) newErrors.keyword = 'Keywords are required';

        if (!formData.content.trim() || formData.content === '<p><br></p>') {
            newErrors.content = 'Content is required';
        }

        // 2MB Size limit check (only if an image is provided)
        if (imageFile && imageFile.size > 2 * 1024 * 1024) {
            newErrors.image = 'Image size must be less than 2MB';
            toast.error('Image size exceeds 2MB limit');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            toast.error('Please fill in all required fields correctly');
            return;
        }

        setIsSubmitting(true);

        try {
            // Use FormData for multipart/form-data support (handles image + data in one request)
            const submissionData = new FormData();
            
            // Append all fields
            submissionData.append('title', formData.title);
            submissionData.append('page_name', 'Blog'); // Hardcoded as per backend requirement
            submissionData.append('page_content', formData.content);
            submissionData.append('created_by', formData.author);
            submissionData.append('keyword', formData.keyword);
            submissionData.append('status', formData.status);
            submissionData.append('page_url', formData.page_url);
            
            // Append image only if a new one is selected
            if (imageFile) {
                submissionData.append('image', imageFile);
            }

            if (id) {
                // For edit mode, use the numeric ID from currentBlog
                if (!currentBlog?.id) {
                    toast.error('Unable to update the blog');
                    setIsSubmitting(false);
                    return;
                }
                await dispatch(updateAdminBlog({ id: currentBlog.id, data: submissionData })).unwrap();
                toast.success('Blog updated successfully!');
            } else {
                // For create mode
                await dispatch(createAdminBlog(submissionData)).unwrap();
                toast.success('Blog created successfully!');
            }

            navigate('/admin/blogs');

        } catch (error) {
            // Handle specific backend errors (e.g. non-unique title)
            const errorMessage = typeof error === 'string' ? error : (error?.message || 'Failed to save blog');
            toast.error(errorMessage);
            
            if (errorMessage.toLowerCase().includes('title has already been taken')) {
                setErrors(prev => ({ ...prev, title: 'This title is already in use' }));
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading && id && !currentBlog) {
        return (
            <div className="flex justify-center items-center h-screen bg-[#ecf0f5]">
                <div className="animate-spin h-12 w-12 border-b-2 border-blue-600 rounded-full"></div>
            </div>
        );
    }

    return (
        <div className="p-6 bg-[#ecf0f5] min-h-screen">

            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold">
                    {id ? 'Edit Blog' : 'Add Blog'}
                </h1>

                <button
                    onClick={() => navigate('/admin/blogs')}
                    className="flex items-center gap-2 bg-[#ffd200] px-4 py-2 rounded font-bold hover:bg-yellow-500 transition"
                >
                    <FontAwesomeIcon icon={faArrowLeft} />
                    Back
                </button>
            </div>

            {/* Form Container */}
            <div className="bg-white p-8 rounded shadow">
                <form onSubmit={handleSubmit}>

                    {/* Row 1: Title & Page URL */}
                    <div className="grid md:grid-cols-2 gap-6 mb-6">
                        <div>
                            <label className="block mb-2 font-medium flex items-center gap-2">
                                Blog Title <span className="text-red-500">*</span>
                                <FontAwesomeIcon icon={faInfoCircle} className="text-gray-400 text-sm" title="Enter the blog title" />
                            </label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                className={`w-full border p-2.5 rounded focus:ring-2 focus:ring-yellow-300 outline-none uppercase ${errors.title ? 'border-red-500' : 'border-gray-300'}`}
                                placeholder="Enter blog title"
                                maxLength="500"
                            />
                            {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
                        </div>

                        <div>
                            <label className="block mb-2 font-medium flex items-center gap-2">
                                Page URL
                                <FontAwesomeIcon icon={faInfoCircle} className="text-gray-400 text-sm" title="Auto-generated from title" />
                            </label>
                            <input
                                type="text"
                                name="page_url"
                                value={formData.page_url}
                                readOnly
                                className="w-full border border-gray-300 p-2.5 rounded bg-gray-100 text-gray-600"
                                placeholder="Auto-generated"
                            />
                        </div>
                    </div>

                    {/* Row 2: Image, Author, Keywords */}
                    <div className="grid md:grid-cols-3 gap-6 mb-6">
                        <div>
                            <label className="block mb-2 font-medium flex items-center gap-2">
                                Blog Image <span className="text-red-500">*</span>
                                <FontAwesomeIcon icon={faInfoCircle} className="text-gray-400 text-sm" title="Upload blog image" />
                            </label>
                            <input 
                                type="file" 
                                name="image" 
                                onChange={handleInputChange}
                                accept="image/*"
                                className="w-full border border-gray-300 p-2 rounded text-sm"
                            />
                            {errors.image && <p className="text-red-500 text-xs mt-1">{errors.image}</p>}
                            
                            {/* Image Preview Section */}
                            <div style={{ marginTop: '5px' }}>
                                {imagePreview && (
                                    <img 
                                        src={imagePreview} 
                                        alt="Preview" 
                                        style={{ width: '150px', height: '100px', marginTop: '5px' }}
                                    />
                                )}
                            </div>
                        </div>

                        <div>
                            <label className="block mb-2 font-medium flex items-center gap-2">
                                Author <span className="text-red-500">*</span>
                                <FontAwesomeIcon icon={faInfoCircle} className="text-gray-400 text-sm" title="Author name" />
                            </label>
                            <input
                                type="text"
                                name="author"
                                value={formData.author}
                                onChange={handleInputChange}
                                className={`w-full border p-2.5 rounded focus:ring-2 focus:ring-yellow-300 outline-none uppercase ${errors.author ? 'border-red-500' : 'border-gray-300'}`}
                                placeholder="Author name"
                            />
                            {errors.author && <p className="text-red-500 text-xs mt-1">{errors.author}</p>}
                        </div>

                        <div>
                            <label className="block mb-2 font-medium flex items-center gap-2">
                                Keywords (SEO) <span className="text-red-500">*</span>
                                <FontAwesomeIcon icon={faInfoCircle} className="text-gray-400 text-sm" title="Enter SEO keywords" />
                            </label>
                            <input
                                type="text"
                                name="keyword"
                                value={formData.keyword}
                                onChange={handleInputChange}
                                className={`w-full border p-2.5 rounded focus:ring-2 focus:ring-yellow-300 outline-none uppercase ${errors.keyword ? 'border-red-500' : 'border-gray-300'}`}
                                placeholder="e.g. jets, luxury, travel"
                            />
                            {errors.keyword && <p className="text-red-500 text-xs mt-1">{errors.keyword}</p>}
                        </div>
                    </div>

                    {/* Row 3: Content Editor */}
                    <div className="mb-6">
                        <label className="block mb-2 font-medium flex items-center gap-2">
                            Content <span className="text-red-500">*</span>
                            <FontAwesomeIcon icon={faInfoCircle} className="text-gray-400 text-sm" title="Enter blog content" />
                        </label>

                        <div className={`border ${errors.content ? 'border-red-500' : 'border-gray-300'}`}>
                            <CKEditor
                                editor={ClassicEditor}
                                data={formData.content}
                                onChange={(event, editor) => {
                                    const data = editor.getData();
                                    setFormData(prev => ({ ...prev, content: data }));

                                    if (errors.content) {
                                        setErrors(prev => ({ ...prev, content: '' }));
                                    }
                                }}
                            />
                        </div>

                        {errors.content && <p className="text-red-500 text-xs mt-1">{errors.content}</p>}
                    </div>

                    {/* Row 4: Action Buttons */}
                    <div className="flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={() => navigate('/admin/blogs')}
                            className="px-6 py-2 bg-gray-300 text-gray-700 rounded font-medium hover:bg-gray-400 transition"
                        >
                            Cancel
                        </button>

                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="px-6 py-2 bg-[#ffd200] text-black rounded font-bold hover:bg-yellow-500 transition disabled:opacity-50"
                        >
                            {isSubmitting ? 'Saving...' : 'Save'}
                        </button>
                    </div>

                </form>
            </div>
        </div>
    );
};

export default BlogEditor;